////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// drag-helpers.ts — pure helpers extracted from DragManager in includes/js/template.js.
// ----------------------------------------------------------------------------------------------------
// Bundled into includes/js/dist/encounter-map-bundle.js. Exposed via
// window.FTEncounterMap.drag.* — DragManager method bodies in template.js are
// 1-line delegate wrappers that preserve the original signature.
//
// Helpers here do not touch `this`, DOM, or fetch. Geometry helpers shared with
// Shared geometry helpers live in ./grid-math. Constants the original
// methods read off template.js module scope (PREFERRED_SPEED_TO_MODE,
// DEFAULT_MOVEMENT_MODE) are passed in by the delegate wrappers so this module
// stays free of ambient globals.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { EncounterArr } from '../shared/array-types';
import { encounterCreatureField } from '../shared/array-types';


// SOURCE: includes/js/template.js:22916-22923 (DragManager._creatureBaseZFromDatasetSize).
// Same ladder as the retired 2-D map widget creature z-index: higher value = smaller creature on the map.
// The original took an `el` and read `el.dataset.size`; the pure version takes the
// size key directly so the wrapper does the DOM read.
export function creatureBaseZFromDatasetSize(sizeKey: string | null | undefined): number {
    const m: Record<string, number> = {
        titanic: 100, colossal: 200, gargantuan: 300,
        huge: 400, large: 500, medium: 600,
        small: 700, tiny: 800,
    };
    return m[sizeKey || 'medium'] ?? 600;
}


// SOURCE: includes/js/template.js:23199-23207 (DragManager._creatureIdSetFromEncounterArr).
// Encounter creature ids in encounterArr (for possess_id chain walks).
export function creatureIdSetFromEncounterArr(
    encounterArr: EncounterArr | null | undefined,
): Set<string> {
    const out = new Set<string>();
    const creatures = encounterArr?.encounter_creatures_arr || [];
    for (let i = 0; i < creatures.length; i++) {
        const id = encounterCreatureField(creatures[i]?.encounter_creatures, 'encounter_creature_id');
        if (id) out.add(id);
    }
    return out;
}


// SOURCE: includes/js/template.js:23252-23263 (DragManager._movementModeFromPreferredSpeedItemId).
// Map preferred_speed_type UUID -> movement mode; unknown speeds default to 'free' (matches
// creature drag). The constants `catalog` (PREFERRED_SPEED_TO_MODE) and `defaultMode`
// (DEFAULT_MOVEMENT_MODE) live in template.js module scope; the delegate wrapper passes
// them in so this helper stays self-contained.
export function movementModeFromPreferredSpeedItemId(
    prefRaw: string | null | undefined,
    catalog: Record<string, string>,
    defaultMode: string,
): string {
    if (!prefRaw) return defaultMode;
    if (Object.prototype.hasOwnProperty.call(catalog, prefRaw)) {
        return catalog[prefRaw] ?? defaultMode;
    }
    const norm = prefRaw.toUpperCase();
    const keys = Object.keys(catalog);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (key && key.toUpperCase() === norm) return catalog[key] ?? 'free';
    }
    return 'free';
}
